import React, { useState, useEffect } from 'react';
import { supabase } from '../../src/lib/supabase';
import { Clock, AlertTriangle, RefreshCcw } from 'lucide-react';
import { getBusinessElapsedMs, getCorrectStartDate } from '../lib/slaUtils';
import { naFilaDaValidacao, slaDaAnalistaConta } from '../lib/validacaoDados';

/** "1d 4h 12m" a partir de minutos. */
const formatarMinutos = (minutos: number) => {
    const days = Math.floor(minutos / (24 * 60));
    const hours = Math.floor((minutos % (24 * 60)) / 60);
    const mins = Math.floor(minutos % 60);
    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0 || days > 0) parts.push(`${hours}h`);
    parts.push(`${mins}m`);
    return parts.join(' ');
};

// Simple module-level cache to prevent multiple DB queries when rendering many cards
let cachedSlas: Record<string, number> | null = null;
let fetchingSlas: Promise<Record<string, number>> | null = null;

interface SlaTimerProps {
    tipo: string; // e.g., 'analise_cadastral'
    startedAt?: string | null;
    status: string;
    minutosRealizados?: number | null;
}

export default function SlaTimer({ tipo, startedAt, status, minutosRealizados }: SlaTimerProps) {
    const [slaHours, setSlaHours] = useState<number | null>(cachedSlas?.[tipo] || null);
    const [timeRemainingStr, setTimeRemainingStr] = useState<string>('');
    const [isBreached, setIsBreached] = useState<boolean>(false);

    // O SLA da analista só conta na primeira análise (src/lib/validacaoDados.ts): na fila e
    // sem a primeira decisão gravada. Voltou para a fila depois de uma decisão = reanálise,
    // e a espera na pendência também não conta contra ela.
    const isActive = tipo === 'analise_cadastral' && slaDaAnalistaConta(status, minutosRealizados);
    const isReanalise = tipo === 'analise_cadastral' && naFilaDaValidacao(status) && !isActive;

    useEffect(() => {
        if (!isActive || !startedAt) return;

        let isMounted = true;

        if (slaHours === null) {
            if (!fetchingSlas) {
                fetchingSlas = Promise.resolve(supabase.from('configuracoes_sla').select('tipo, tempo_horas').then(({data, error}) => {
                    const map: Record<string, number> = {};
                    if (!error && data) {
                        data.forEach(d => map[d.tipo] = d.tempo_horas);
                    }
                    cachedSlas = map;
                    return map;
                }));
            }
            fetchingSlas.then(map => {
                if (isMounted) {
                    setSlaHours(map[tipo] || 48); // default to 48h if not found to prevent breaking
                }
            });
        }

        return () => { isMounted = false; };
    }, [tipo, slaHours, isActive, startedAt]);

    useEffect(() => {
        if (!isActive || !startedAt || !slaHours) return;

        const calculateTime = () => {
            const start = getCorrectStartDate(startedAt);
            if (!start) return;

            const now = new Date();
            const slaMs = slaHours * 60 * 60 * 1000;
            const elapsedBusinessMs = getBusinessElapsedMs(start, now);
            
            const diff = slaMs - elapsedBusinessMs;
            const breached = diff < 0;
            setIsBreached(breached);

            const absDiff = Math.abs(diff);
            const days = Math.floor(absDiff / (1000 * 60 * 60 * 24));
            const hours = Math.floor((absDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const mins = Math.floor((absDiff % (1000 * 60 * 60)) / (1000 * 60));

            const parts = [];
            if (days > 0) parts.push(`${days}d`);
            if (hours > 0 || days > 0) parts.push(`${hours}h`);
            parts.push(`${mins}m`);

            setTimeRemainingStr(parts.join(' '));
        };

        // Initial calc
        calculateTime();
        
        // Update every 30 seconds to save performance, 1 min is also fine, let's do 10s
        const interval = setInterval(calculateTime, 10000);

        return () => clearInterval(interval);
    }, [startedAt, slaHours, isActive]);
    
    if (isReanalise) {
        return (
            <div
                className="flex flex-wrap items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-bold w-fit bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800/50"
                title={`Reanálise: a primeira análise já foi feita${minutosRealizados != null ? ` (em ${formatarMinutos(minutosRealizados)})` : ''}. O prazo de SLA vale só para a primeira análise e não conta de novo.`}
            >
                <RefreshCcw size={12} />
                <span>Reanálise · sem SLA</span>
            </div>
        );
    }

    if (!isActive) {
        // Fora da fila: mostra quanto levou a primeira análise, quando gravada.
        if (minutosRealizados != null) {
            return (
                <div
                    className="flex flex-wrap items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-bold w-fit bg-gray-100 text-gray-600 border border-gray-200 dark:bg-gray-800/50 dark:text-gray-400 dark:border-gray-700"
                    title="Tempo útil da assinatura do termo até a primeira decisão da analista (é o que conta no SLA)."
                >
                    <Clock size={12} />
                    <span>1ª análise em: {formatarMinutos(minutosRealizados)}</span>
                </div>
            );
        }
        return null;
    }

    if (!startedAt || slaHours === null) return null;

    return (
        <div 
            className={`flex flex-wrap items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-bold w-fit
            ${isBreached 
                ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800/50' 
                : 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/50'}`}
            title={isBreached ? "O prazo para conclusão desta etapa foi excedido!" : "Tempo restante dentro do prazo SLA."}
        >
            {isBreached ? <AlertTriangle size={12} className="animate-pulse" /> : <Clock size={12} />}
            <span>
                {isBreached ? 'Atrasado: ' : 'SLA: '} 
                {timeRemainingStr}
            </span>
        </div>
    );
}
